import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Box, Button, Flex, Heading, LoadingOrbit, NumberField, Text } from '@chatsdet/ui';
import {
  BROWSER_VIEWPORT_PRESETS,
  DEFAULT_BROWSER_ENGINE,
  DEFAULT_BROWSER_VIEWPORT_PRESET,
  isBrowserEngine,
  isBrowserViewportPreset,
  type BrowserEngine,
  type BrowserViewportPreset,
} from '@chatsdet/types';
import { API_URL } from '../../../config.js';
import { toApiError } from '../../../lib/api-error';
import { BrowserEnginePicker } from '../BrowserEnginePicker';
import { ViewportPicker } from '../ViewportPicker';

// Keep these in sync with the server bounds in
// apps/api/src/browser-session/session-timeout.ts.
const LOGIN_TIMEOUT_SECONDS_MIN = 60;
const LOGIN_TIMEOUT_SECONDS_MAX = 600;
const DEFAULT_LOGIN_TIMEOUT_SECONDS = 180;

type AgentBrowserSettings = {
  defaultBrowserEngine: BrowserEngine;
  defaultBrowserViewport: BrowserViewportPreset;
  loginTimeoutSeconds: number;
};

type AgentBrowserSettingsResponse = {
  ok: boolean;
  data: {
    defaultBrowserEngine: string;
    defaultBrowserViewport: string;
    loginTimeoutSeconds: number;
  };
};

type AgentBrowserSectionProps = {
  isAdmin: boolean;
};

function coercePreset(value: unknown): BrowserViewportPreset {
  return isBrowserViewportPreset(value) ? value : DEFAULT_BROWSER_VIEWPORT_PRESET;
}

function coerceEngine(value: unknown): BrowserEngine {
  return isBrowserEngine(value) ? value : DEFAULT_BROWSER_ENGINE;
}

function coerceTimeout(value: unknown): number {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return DEFAULT_LOGIN_TIMEOUT_SECONDS;
  }
  return Math.min(
    LOGIN_TIMEOUT_SECONDS_MAX,
    Math.max(LOGIN_TIMEOUT_SECONDS_MIN, Math.round(value))
  );
}

export function AgentBrowserSection({ isAdmin }: AgentBrowserSectionProps) {
  const queryClient = useQueryClient();
  const [engineDraft, setEngineDraft] = React.useState<BrowserEngine | null>(null);
  const [viewportDraft, setViewportDraft] = React.useState<BrowserViewportPreset | null>(null);
  const [timeoutDraft, setTimeoutDraft] = React.useState<number | null>(null);

  const settingsQuery = useQuery({
    queryKey: ['org-agent-browser-settings'],
    queryFn: async (): Promise<AgentBrowserSettingsResponse> => {
      const response = await fetch(`${API_URL}/org/settings`, { credentials: 'include' });
      if (!response.ok) throw new Error('Failed to load agent browser settings');
      return response.json();
    },
  });

  React.useEffect(() => {
    if (settingsQuery.data?.data) {
      setEngineDraft(coerceEngine(settingsQuery.data.data.defaultBrowserEngine));
      setViewportDraft(coercePreset(settingsQuery.data.data.defaultBrowserViewport));
      setTimeoutDraft(coerceTimeout(settingsQuery.data.data.loginTimeoutSeconds));
    }
  }, [settingsQuery.data]);

  const updateMutation = useMutation({
    mutationFn: async (payload: AgentBrowserSettings) => {
      const response = await fetch(`${API_URL}/org/settings`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw await toApiError(response, 'Failed to update agent browser settings');
      const data = await response.json();
      return data as AgentBrowserSettingsResponse;
    },
    onSuccess: ({ data }) => {
      setEngineDraft(coerceEngine(data.defaultBrowserEngine));
      setViewportDraft(coercePreset(data.defaultBrowserViewport));
      setTimeoutDraft(coerceTimeout(data.loginTimeoutSeconds));
      void queryClient.invalidateQueries({ queryKey: ['org-agent-browser-settings'] });
    },
  });

  if (settingsQuery.isLoading) {
    return (
      <Flex align="center" justify="center" className="py-12">
        <LoadingOrbit size="md" />
      </Flex>
    );
  }

  if (
    settingsQuery.isError ||
    engineDraft === null ||
    viewportDraft === null ||
    timeoutDraft === null
  ) {
    return (
      <Box className="space-y-6">
        <div className="space-y-1">
          <Heading level={2}>Agent Browser</Heading>
          <Text size="sm" className="text-muted-foreground">
            Failed to load agent browser settings.
          </Text>
        </div>
        <Flex justify="start">
          <Button variant="outline" onPress={() => settingsQuery.refetch()}>
            Retry
          </Button>
        </Flex>
      </Box>
    );
  }

  const savedEngine = coerceEngine(settingsQuery.data?.data.defaultBrowserEngine);
  const savedViewport = coercePreset(settingsQuery.data?.data.defaultBrowserViewport);
  const savedTimeout = coerceTimeout(settingsQuery.data?.data.loginTimeoutSeconds);
  const hasChanges =
    engineDraft !== savedEngine || viewportDraft !== savedViewport || timeoutDraft !== savedTimeout;
  const disabled = !isAdmin || updateMutation.isPending;

  return (
    <Box className="space-y-8">
      <div className="space-y-1">
        <Heading level={2}>Agent Browser</Heading>
        <Text size="sm" className="text-muted-foreground">
          How this organization&apos;s agent browser sessions start and behave
        </Text>
      </div>

      <div className="space-y-3">
        <Text
          size="xs"
          weight="semibold"
          className="uppercase tracking-wider text-muted-foreground"
        >
          Default browser engine
        </Text>

        <Text size="sm" className="text-muted-foreground">
          Choose which browser engine agent sessions use by default. Individual runs can override it
          from the Flow Designer&apos;s <span className="font-medium">Run with config</span> menu.
        </Text>

        <BrowserEnginePicker
          value={engineDraft}
          onChange={setEngineDraft}
          isDisabled={disabled}
          aria-label="Default browser engine"
        />
      </div>

      <div className="space-y-3">
        <div className="flex items-baseline justify-between">
          <Text
            size="xs"
            weight="semibold"
            className="uppercase tracking-wider text-muted-foreground"
          >
            Default viewport
          </Text>
          <Text size="xs" className="text-muted-foreground tabular-nums">
            {BROWSER_VIEWPORT_PRESETS[viewportDraft].width}×
            {BROWSER_VIEWPORT_PRESETS[viewportDraft].height}
          </Text>
        </div>

        <Text size="sm" className="text-muted-foreground">
          Agents drive a browser at this size by default. Individual runs can override it from the
          Flow Designer&apos;s <span className="font-medium">Run with config</span> menu.
        </Text>

        <ViewportPicker
          value={viewportDraft}
          onChange={setViewportDraft}
          isDisabled={disabled}
          aria-label="Default viewport size"
        />
      </div>

      <div className="space-y-3">
        <Text
          size="xs"
          weight="semibold"
          className="uppercase tracking-wider text-muted-foreground"
        >
          Login timeout
        </Text>

        <NumberField
          label="Login timeout (seconds)"
          value={timeoutDraft}
          onChange={(value) => setTimeoutDraft(value ?? DEFAULT_LOGIN_TIMEOUT_SECONDS)}
          minValue={LOGIN_TIMEOUT_SECONDS_MIN}
          maxValue={LOGIN_TIMEOUT_SECONDS_MAX}
          step={30}
          showStepper
          isDisabled={disabled}
          className="max-w-xs"
          description={`Max time the agent waits for login / session startup before failing. Raise this for apps whose login requires multiple email or verification steps. Default ${DEFAULT_LOGIN_TIMEOUT_SECONDS}s (${DEFAULT_LOGIN_TIMEOUT_SECONDS / 60} min); allowed ${LOGIN_TIMEOUT_SECONDS_MIN}–${LOGIN_TIMEOUT_SECONDS_MAX}s.`}
        />
      </div>

      {!isAdmin && (
        <Text size="sm" className="text-muted-foreground">
          Only organization admins can update agent browser settings.
        </Text>
      )}

      <Flex justify="end">
        <Button
          onPress={() =>
            updateMutation.mutate({
              defaultBrowserEngine: engineDraft,
              defaultBrowserViewport: viewportDraft,
              loginTimeoutSeconds: timeoutDraft,
            })
          }
          isDisabled={disabled || !hasChanges}
        >
          {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
        </Button>
      </Flex>
    </Box>
  );
}
